import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as api from './api';
import type {
  AssignHomeworkInput,
  CreateLinkArtifactInput,
  GradeSubmissionInput,
  HomeworkListParams,
  HomeworkWriteInput,
  SubmissionListParams,
} from './types';

/** Query-key factories — every homework key derives from these. */
export const homeworkKeys = {
  all: ['homework'] as const,
  lists: () => [...homeworkKeys.all, 'list'] as const,
  list: (params: HomeworkListParams) => [...homeworkKeys.lists(), params] as const,
  details: () => [...homeworkKeys.all, 'detail'] as const,
  detail: (publicId: string) => [...homeworkKeys.details(), publicId] as const,
};

export const submissionKeys = {
  all: ['submissions'] as const,
  lists: () => [...submissionKeys.all, 'list'] as const,
  list: (params: SubmissionListParams) => [...submissionKeys.lists(), params] as const,
  details: () => [...submissionKeys.all, 'detail'] as const,
  detail: (publicId: string) => [...submissionKeys.details(), publicId] as const,
  /** The pupil's own get-or-create draft for one homework. */
  mine: (homeworkId: string) => [...submissionKeys.all, 'mine', homeworkId] as const,
};

// ---------------------------------------------------------------------------
// Homework
// ---------------------------------------------------------------------------

export function useHomeworkList(
  params: HomeworkListParams = {},
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: homeworkKeys.list(params),
    queryFn: () => api.getHomeworkList(params),
    placeholderData: keepPreviousData,
    enabled: options.enabled,
  });
}

export function useHomework(publicId: string) {
  return useQuery({
    queryKey: homeworkKeys.detail(publicId),
    queryFn: () => api.getHomework(publicId),
    enabled: Boolean(publicId),
  });
}

export function useCreateHomework() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: HomeworkWriteInput) => api.createHomework(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: homeworkKeys.lists() }),
  });
}

export function useUpdateHomework(publicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: HomeworkWriteInput) => api.updateHomework(publicId, input),
    onSuccess: detail => {
      qc.setQueryData(homeworkKeys.detail(publicId), detail);
      void qc.invalidateQueries({ queryKey: homeworkKeys.lists() });
    },
  });
}

export function useArchiveHomework() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.archiveHomework(publicId),
    onSuccess: (_data, publicId) => {
      void qc.invalidateQueries({ queryKey: homeworkKeys.lists() });
      void qc.invalidateQueries({ queryKey: homeworkKeys.detail(publicId) });
    },
  });
}

export function usePublishHomework() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.publishHomework(publicId),
    onSuccess: detail => {
      qc.setQueryData(homeworkKeys.detail(detail.public_id), detail);
      void qc.invalidateQueries({ queryKey: homeworkKeys.lists() });
    },
  });
}

export function useUnpublishHomework() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.unpublishHomework(publicId),
    onSuccess: detail => {
      qc.setQueryData(homeworkKeys.detail(detail.public_id), detail);
      void qc.invalidateQueries({ queryKey: homeworkKeys.lists() });
    },
  });
}

export function useDuplicateHomework() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId }: { publicId: string }) => api.duplicateHomework(publicId),
    onSuccess: copy => {
      qc.setQueryData(homeworkKeys.detail(copy.public_id), copy);
      void qc.invalidateQueries({ queryKey: homeworkKeys.lists() });
    },
  });
}

export function useAssignHomework(publicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AssignHomeworkInput) => api.assignHomework(publicId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: homeworkKeys.detail(publicId) });
      void qc.invalidateQueries({ queryKey: homeworkKeys.lists() });
    },
  });
}

export function useUnassignHomework(publicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (schoolClass: string) => api.unassignHomework(publicId, schoolClass),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: homeworkKeys.detail(publicId) });
      void qc.invalidateQueries({ queryKey: homeworkKeys.lists() });
    },
  });
}

// ---------------------------------------------------------------------------
// Submissions
// ---------------------------------------------------------------------------

export function useSubmissions(
  params: SubmissionListParams = {},
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: submissionKeys.list(params),
    queryFn: () => api.getSubmissions(params),
    placeholderData: keepPreviousData,
    enabled: options.enabled,
  });
}

/**
 * The pupil's own submission for one homework. The backend exposes no GET for
 * "my submission", but `POST /submissions/` is an explicit get-or-create
 * (200 when it already existed), so it doubles as the read — the same shape as
 * the join-code card in FE-ADR-010.
 */
export function useMySubmission(homeworkId: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: submissionKeys.mine(homeworkId),
    queryFn: () => api.openSubmission(homeworkId),
    enabled: (options.enabled ?? true) && Boolean(homeworkId),
  });
}

export function useUpdateSubmissionText(homeworkId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, text }: { publicId: string; text: string }) =>
      api.updateSubmissionText(publicId, text),
    onSuccess: submission => {
      qc.setQueryData(submissionKeys.mine(homeworkId), submission);
      void qc.invalidateQueries({ queryKey: submissionKeys.lists() });
    },
  });
}

export function useSubmitSubmission(homeworkId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.submitSubmission(publicId),
    onSuccess: submission => {
      qc.setQueryData(submissionKeys.mine(homeworkId), submission);
      void qc.invalidateQueries({ queryKey: submissionKeys.lists() });
    },
  });
}

export function useGradeSubmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, input }: { publicId: string; input: GradeSubmissionInput }) =>
      api.gradeSubmission(publicId, input),
    onSuccess: submission => {
      qc.setQueryData(submissionKeys.detail(submission.public_id), submission);
      void qc.invalidateQueries({ queryKey: submissionKeys.lists() });
    },
  });
}

export function useReturnSubmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, feedback }: { publicId: string; feedback: string }) =>
      api.returnSubmission(publicId, feedback),
    onSuccess: submission => {
      qc.setQueryData(submissionKeys.detail(submission.public_id), submission);
      void qc.invalidateQueries({ queryKey: submissionKeys.lists() });
    },
  });
}

/** Artifact writes refresh the owning submission (the artifacts list is nested
 *  in its payload). Uploads are called imperatively for progress reporting. */
export function useDeleteSubmissionArtifact(homeworkId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.deleteSubmissionArtifact(publicId),
    onSuccess: () => {
      if (homeworkId) {
        void qc.invalidateQueries({ queryKey: submissionKeys.mine(homeworkId) });
      }
      void qc.invalidateQueries({ queryKey: submissionKeys.lists() });
    },
  });
}

export function useCreateLinkArtifact(homeworkId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLinkArtifactInput) => api.createLinkArtifact(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: submissionKeys.mine(homeworkId) }),
  });
}
